import { GameEventType } from '../interfaces/game-details';

/**
 * Тип события в протоколе ↔ тип события на бэкенде.
 *
 * У бэкенда **две разные формы** одного типа:
 * - на запись (`MatchEventInput.type`) — GraphQL-enum `MatchEventType`: `GOAL`, `YELLOW_CARD`, `TIME_OUT`;
 * - на чтение (`MatchEvent.type: String`) — своя строчная форма: `goal`, `yellowcard`, `timeout`,
 *   `owngoal`, `penalty_missed`, `after_match_penalty_goal`.
 *
 * В `GameEventType` значения смешанные (`GOAL`, но `'owngoal'`), поэтому сырой тип из ответа нельзя
 * ни класть в событие, ни отправлять обратно: при сохранении матча строчный `goal` бэкенд отвергает
 * («Cannot represent enum … from value goal»), и `upsertMatch` падает целиком. Раньше неузнанный тип
 * молча становился `GOAL`, и каждое пересохранение превращало карточки и фолы в голы.
 */
const BACKEND_BY_EVENT_TYPE: Record<GameEventType, string> = {
  [GameEventType.GOAL]: 'GOAL',
  // «Пас и гол» — это гол с `secondaryUserId`; отдельного типа на бэкенде нет.
  [GameEventType.PASS_AND_GOAL]: 'GOAL',
  [GameEventType.YELLOW_CARD]: 'YELLOW_CARD',
  [GameEventType.RED_CARD]: 'RED_CARD',
  [GameEventType.FOUL]: 'FOUL',
  [GameEventType.PENALTY]: 'PENALTY',
  [GameEventType.SAVE]: 'SAVE',
  [GameEventType.TIMEOUT]: 'TIME_OUT',
  [GameEventType.REPLACEMENT]: 'REPLACEMENT',
  [GameEventType.OWN_GOAL]: 'OWN_GOAL',
  [GameEventType.CORNER]: 'CORNER',
  [GameEventType.AFTER_MATCH_PENALTY_GOAL]: 'AFTER_MATCH_PENALTY_GOAL',
  [GameEventType.AFTER_MATCH_PENALTY_MISSED]: 'AFTER_MATCH_PENALTY_MISSED',
  [GameEventType.DEFAULT]: 'GOAL',
};

/** `YELLOW_CARD`, `yellow_card` и `yellowcard` — один ключ `yellowcard`. */
const squash = (type: string) => type.toLowerCase().replace(/_/g, '');

const EVENT_TYPE_BY_SQUASHED: Record<string, GameEventType> = Object.fromEntries(
  (Object.entries(BACKEND_BY_EVENT_TYPE) as [GameEventType, string][])
    .filter(([eventType]) => eventType !== GameEventType.PASS_AND_GOAL && eventType !== GameEventType.DEFAULT)
    .map(([eventType, backendType]) => [squash(backendType), eventType]),
);

function isGameEventType(type: string): type is GameEventType {
  return Object.prototype.hasOwnProperty.call(BACKEND_BY_EVENT_TYPE, type);
}

/**
 * Тип из ответа бэкенда (в любой из его форм) → `GameEventType`. Тип, которого в протоколе нет
 * (`penalty_missed`, `kills` — их пишет трансляция), остаётся как пришёл: `mapEventTypeForBackend`
 * отправит его заглавным именем enum-а, и пересохранение матча не превратит чужое событие в гол.
 */
export function mapEventTypeFromBackend(type: string): GameEventType {
  return EVENT_TYPE_BY_SQUASHED[squash(type)] ?? (type as GameEventType);
}

export function mapEventTypeForBackend(type: GameEventType | string): string {
  if (isGameEventType(type)) {
    return BACKEND_BY_EVENT_TYPE[type];
  }

  const known = EVENT_TYPE_BY_SQUASHED[squash(type)];
  if (known) {
    return BACKEND_BY_EVENT_TYPE[known];
  }

  // Чтение отдаёт `penalty_missed`, enum на запись ждёт `PENALTY_MISSED`.
  return type.toUpperCase();
}
